import { useRef, useState } from 'react';
import { FileUp, Sparkles, X } from 'lucide-react';
import { Button, Spinner } from '../../../school_panel/components/ui';
import { SchoolApi } from '../../../school_panel/api/client';
import type { ImportedQuestion } from '../../../school_panel/api/types';

const ACCEPT = '.pdf,.docx,.txt,image/*';
const MAX_FILES = 10;

const errText = (err: unknown) => {
  const r = (err as { response?: { status?: number; data?: { detail?: string; message?: string } } })?.response;
  if (r?.status === 429) return r.data?.message || r.data?.detail || 'Your AI budget for this month is used up.';
  return r?.data?.detail || 'The files could not be read. Try again.';
};

/** "Import with AI": the teacher drops PDFs, Word files or photos; the AI returns typed questions to review. */
export default function ImportPanel({ examId, onExtracted }: { examId: number; onExtracted: (qs: ImportedQuestion[]) => void }) {
  const input = useRef<HTMLInputElement>(null);
  const [files, setFiles] = useState<File[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const add = (list: FileList | null) => {
    if (!list) return;
    setError(null);
    setFiles((cur) => [...cur, ...Array.from(list)].slice(0, MAX_FILES));
  };

  const read = async () => {
    setBusy(true); setError(null);
    try {
      const qs = await SchoolApi.authoringImport(examId, files);
      if (qs.length === 0) setError('No questions were found in these files.');
      else { onExtracted(qs); setFiles([]); }
    } catch (err) {
      setError(errText(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section aria-label="Import with AI" className="rounded-[20px] border border-line bg-surface p-5 shadow-card">
      <div className="mb-3 flex items-start gap-3">
        <span aria-hidden className="grid h-10 w-10 flex-none place-items-center rounded-[12px] bg-accent text-navy"><Sparkles size={18} /></span>
        <div className="min-w-0">
          <h2 className="font-display text-[15px] font-bold text-ink">Import with AI</h2>
          <p className="text-[13px] text-muted">From a PDF, Word file or photos. The AI finds each question and its type; you check them before they are added.</p>
        </div>
      </div>
      <div
        onDragOver={(e) => e.preventDefault()}
        onDrop={(e) => { e.preventDefault(); add(e.dataTransfer.files); }}
        className="rounded-[14px] border-[1.5px] border-dashed border-line-strong bg-surface-2 px-4 py-5 text-center">
        <FileUp size={22} aria-hidden className="mx-auto text-muted" />
        <p className="mt-1.5 text-[13px] text-muted">Drop files here or</p>
        <Button type="button" size="sm" variant="secondary" className="mt-2" onClick={() => input.current?.click()} disabled={busy}>Choose files</Button>
        <input ref={input} type="file" multiple accept={ACCEPT} className="sr-only" aria-label="Files to import"
          onChange={(e) => { add(e.target.files); e.target.value = ''; }} />
        <p className="mt-2 text-xs text-muted">PDF, Word (.docx), text or images · up to {MAX_FILES} files, 25 MB</p>
      </div>
      {files.length > 0 && (
        <ul className="mt-3 space-y-1.5" aria-label="Files to read">
          {files.map((f, i) => (
            <li key={`${f.name}-${i}`} className="flex items-center gap-2 rounded-[10px] bg-surface-2 px-3 py-2 text-[13px] text-ink">
              <span className="min-w-0 flex-1 truncate">{f.name}</span>
              <span className="font-mono text-xs text-muted">{Math.max(1, Math.round(f.size / 1024))} KB</span>
              <button type="button" aria-label={`Remove ${f.name}`} disabled={busy} onClick={() => setFiles((cur) => cur.filter((_, j) => j !== i))}
                className="grid h-7 w-7 place-items-center rounded-[8px] text-muted hover:bg-surface hover:text-ink"><X size={14} aria-hidden /></button>
            </li>
          ))}
        </ul>
      )}
      {error && <p role="alert" className="mt-3 rounded-[12px] bg-danger-bg px-3 py-2 text-sm text-danger-ink">{error}</p>}
      {files.length > 0 && (
        <Button className="mt-3 w-full" onClick={read} loading={busy} icon={busy ? undefined : <Sparkles size={16} aria-hidden />}>
          {busy ? 'Reading the files…' : `Read questions from ${files.length} ${files.length === 1 ? 'file' : 'files'}`}
        </Button>
      )}
      {busy && <p className="mt-2 flex items-center justify-center gap-2 text-xs text-muted"><Spinner size="sm" /> This can take up to a minute for long files.</p>}
    </section>
  );
}
